import './EventsList.scss';
import '../../Admin.scss';
import '../../../../styles/_table.scss';

import { AdminEventSortKeys, eventDataSorter } from 'utilities/dataSorters';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AdminEventModel } from 'types/admin/trackmanEventsAdmin';
import { AdminPagesRelativeRoutes, DefaultRoutes } from 'Routes';
import DeleteListItemModal from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import HeaderSearchbar from 'components/Admin/HeaderSearchbar/HeaderSearchbar';
import { Link } from 'react-router-dom';
import Paragraph from 'components/TextComps/Paragraph';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import SortOrderButton from 'components/SortOrderButton/SortOrderButton';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { ReactComponent as WarningIcon } from 'images/icons/Warning.svg';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { useStores } from 'index';

type RowsDisplay = {
  id: string;
  title: string;
  membersOnly: string;
  state: string;
  visible: string;
  languages: string;
  isUpcomingEventPassed: boolean;
  isRecordingMissing: boolean;
  createdAt: string;
  updatedAt: string;
};

export const EventsList = observer(() => {
  useSetPageTitle({ title: 'Events List' });
  const es = useStores().trackmanEventsEditorStore;
  const [isModalOn, setIsModalOn] = useState(false);
  const [sortByProperty, setSortByProperty] = useState<AdminEventSortKeys>('title');
  const [sortOrder, setSortOrder] = useState<'ASCENDING' | 'DESCENDING'>('ASCENDING');

  useEffect(() => {
    es.eventsList.length === 0 && es.getEvents();
    setSortByProperty('title');
    setSortOrder('ASCENDING');
  }, [es]);

  useEffect(() => {
    let filteredList = es.eventsList.filter((event) => event.title.toLowerCase().includes(es.searchValue.toLowerCase()));

    if (sortByProperty) {
      filteredList = eventDataSorter(filteredList, sortByProperty, sortOrder);
    }

    es.filteredList = filteredList;
  }, [es, es.searchValue, sortByProperty, sortOrder]);

  const rowsDisplay: RowsDisplay[] = useMemo(
    () =>
      es.filteredList.map((eventItem: AdminEventModel) => {
        return {
          id: eventItem.id,
          title: eventItem.title,
          membersOnly: eventItem.premiumEvent ? 'Yes' : 'No',
          state: eventItem.recordingReady
            ? 'On demand'
            : !eventItem.recordingReady && new Date(eventItem.startDate) < new Date()
              ? 'Event has passed'
              : 'Upcoming',
          visible: eventItem.visible ? 'Yes' : 'No',
          isUpcomingEventPassed: !eventItem.recordingReady && new Date(eventItem.startDate) < new Date(),
          isRecordingMissing:
            !eventItem.recordingReady && eventItem.locationType === 'Online' && new Date(eventItem.startDate) < new Date(),
          createdAt: dayjs(eventItem.createdAt).format('MMM DD, YYYY'),
          updatedAt: dayjs(eventItem.updatedAt).format('MMM DD, YYYY'),
        };
      }) as RowsDisplay[],
    [es.filteredList]
  );

  const handleSortChange = (property: AdminEventSortKeys, order: 'ASCENDING' | 'DESCENDING') => {
    setSortByProperty(property as AdminEventSortKeys);
    setSortOrder(order);
  };

  const deleteEvent = useCallback(() => {
    es.deleteEventAndUpdate();
    setIsModalOn(false);
  }, [es, setIsModalOn]);

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>Events List</h1>
      </div>
      <div className='admin-content-container'>
        <div className='admin-list-header'>
          <HeaderSearchbar searchValue={es.searchValue} onChange={es.setSearchValue} />
          <HeaderCreateButton buttonTitle='New event' route={AdminPagesRelativeRoutes.EventEditor} />
        </div>

        <div className='tmu-table-wrapper events-table-wrapper'>
          <Table className='tmu-table admin-table'>
            <TableHead>
              <TableRow>
                <TableCell>
                  Name
                  <SortOrderButton
                    isActive={sortByProperty === 'title'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('title', sortOrder)}
                  />
                </TableCell>
                <TableCell>
                  Members only
                  <SortOrderButton
                    isActive={sortByProperty === 'premiumEvent'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('premiumEvent', sortOrder)}
                  />
                </TableCell>
                <TableCell>
                  State
                  <SortOrderButton
                    isActive={sortByProperty === 'state'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('state', sortOrder)}
                  />
                </TableCell>
                <TableCell>
                  Visible
                  <SortOrderButton
                    isActive={sortByProperty === 'visible'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('visible', sortOrder)}
                  />
                </TableCell>
                <TableCell>
                  Date created
                  <SortOrderButton
                    isActive={sortByProperty === 'createdAt'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('createdAt', sortOrder)}
                  />
                </TableCell>
                <TableCell>
                  Last edited
                  <SortOrderButton
                    isActive={sortByProperty === 'updatedAt'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('updatedAt', sortOrder)}
                  />
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsDisplay.length ? (
                rowsDisplay.map((row) => {
                  return (
                    <TableRow hover tabIndex={-1} key={row.id} className={`${row.isRecordingMissing ? 'passed-event' : ''}`}>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>{row.membersOnly}</TableCell>
                      <TableCell>
                        {row.isRecordingMissing ? (
                          <div className='expiry-warning'>
                            <WarningIcon />
                            <span>Missing recording</span>
                          </div>
                        ) : (
                          <div className={`${row.state === 'Upcoming' ? 'upcoming' : 'on-demand'}`}>{row.state}</div>
                        )}
                      </TableCell>
                      <TableCell>{row.visible}</TableCell>
                      <TableCell>{row.createdAt}</TableCell>
                      <TableCell>{row.updatedAt}</TableCell>
                      <TableCell>
                        <div className='action-buttons'>
                          <Link to={`/Admin/${AdminPagesRelativeRoutes.EventEditor}/${row.id}`} className='row-button'>
                            <PencilIcon />
                          </Link>
                          <button
                            className={`row-button ${es.isLoading ? 'disabled' : ''}`}
                            onClick={() => {
                              es.selectedEventId = row.id;
                              setIsModalOn(true);
                            }}
                            disabled={es.isLoading}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Paragraph>
                      <>No events found</>
                    </Paragraph>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <DeleteListItemModal isModalOn={isModalOn} setIsModalOn={setIsModalOn} deleteAction={deleteEvent} itemName='event' />
      </div>
    </>
  );
});

export default EventsList;
